/** Sites and their location trees. */
import { useSearch, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type Location,
  type LocationIn,
  type LocationPatch,
  type LocationType,
  SITE_KINDS,
  type Site,
  type SiteIn,
  type SitePatch,
  useCreateLocationType,
  useDeleteLocation,
  useLocationTypes,
  useLocationsOf,
  useSaveLocation,
  useSaveSite,
  useSites,
} from "@/features/admin/reference/queries";
import {
  AttributeFields,
  type AttributeValues,
  compactAttributes,
  useAttributeDefinitions,
  withDefaults,
} from "@/shared/components/attribute-fields";
import { FormDialog } from "@/shared/components/form-dialog";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { formatPoint } from "@/shared/lib/format";
import { nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select } from "@/shared/ui/form";
import { Badge, Card, CardContent, CardHeader, CardTitle, Dialog } from "@/shared/ui/primitives";

interface SiteValues extends Record<string, unknown> {
  code: string;
  name: string;
  kind: string;
  timezone: string;
  lat: string;
  lon: string;
  address_line: string;
  city: string;
  country: string;
  is_active: boolean;
}

/** Site create/edit dialog. */
export function SiteDialog({ site, onClose }: { site?: Site; onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const save = useSaveSite();
  const defs = useAttributeDefinitions("site");
  const [attributes, setAttributes] = useState<AttributeValues>(() =>
    withDefaults(defs.data ?? [], site?.attributes),
  );
  const address = (site?.address ?? {}) as Record<string, string>;
  const initial = useMemo<SiteValues>(
    () => ({
      code: site?.code ?? "",
      name: site?.name ?? "",
      kind: site?.kind ?? "yard",
      timezone: site?.timezone ?? "",
      lat: site?.geo ? String(site.geo.lat) : "",
      lon: site?.geo ? String(site.geo.lon) : "",
      address_line: address.line1 ?? "",
      city: address.city ?? "",
      country: address.country ?? "",
      is_active: site?.is_active ?? true,
    }),
    [site, address.line1, address.city, address.country],
  );
  const form = useForm<SiteValues>({
    initial,
    schema: z.object({
      code: site ? z.string() : z.string().trim().min(1, t("app.required")),
      name: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "site",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const geo = v.lat && v.lon ? { lat: Number(v.lat), lon: Number(v.lon) } : null;
      const body: SiteIn | SitePatch = {
        ...(site ? {} : { code: v.code.trim() }),
        name: v.name.trim(),
        kind: v.kind,
        timezone: v.timezone,
        geo,
        address: { line1: v.address_line, city: v.city, country: v.country },
        attributes: compactAttributes(defs.data ?? [], attributes),
        is_active: v.is_active,
      };
      await save.mutateAsync({ id: site?.id, body });
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={site ? t("sites.editSite") : t("sites.newSite")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="site-code" label={t("app.code")} error={form.errors.code}>
          <Input {...form.field("code")} disabled={!!site} />
        </Field>
        <Field id="site-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        <Field id="site-kind" label={t("app.type")}>
          <Select
            id="site-kind"
            value={form.values.kind}
            onChange={(e) => form.set("kind", e.target.value)}
          >
            {SITE_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`sites.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="site-timezone" label={t("sites.timezone")} hint="e.g. Asia/Riyadh">
          <Input {...form.field("timezone")} />
        </Field>
        <Field id="site-lat" label={t("assets.latitude")} error={form.errors.lat}>
          <Input type="number" step="any" {...form.field("lat")} />
        </Field>
        <Field id="site-lon" label={t("assets.longitude")} error={form.errors.lon}>
          <Input type="number" step="any" {...form.field("lon")} />
        </Field>
        <Field id="site-address_line" label={t("sites.address")}>
          <Input {...form.field("address_line")} />
        </Field>
        <Field id="site-city" label={t("sites.city")}>
          <Input {...form.field("city")} />
        </Field>
        <Field id="site-country" label={t("sites.country")}>
          <Input {...form.field("country")} />
        </Field>
      </div>
      {defs.data?.length ? (
        <AttributeFields
          definitions={defs.data}
          values={attributes}
          onChange={setAttributes}
          idPrefix="site-attr"
        />
      ) : null}
      <Checkbox
        id="site-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}

interface LocationValues extends Record<string, unknown> {
  code: string;
  name: string;
  type_id: string;
  parent_id: string;
  capacity: string;
  is_stock_location: boolean;
  is_bookable: boolean;
  is_active: boolean;
}

/** Location create/edit dialog. */
export function LocationDialog({
  site,
  locations,
  location,
  parent,
  onClose,
}: {
  site: Site;
  locations: Location[];
  location?: Location;
  parent?: Location;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveLocation();
  const types = useLocationTypes();
  const initial = useMemo<LocationValues>(
    () => ({
      code: location?.code ?? "",
      name: location?.name ?? "",
      type_id: location?.type_id ?? "",
      parent_id: location?.parent_id ?? parent?.id ?? "",
      capacity:
        location?.capacity === null || location?.capacity === undefined
          ? ""
          : String(location.capacity),
      is_stock_location: location?.is_stock_location ?? false,
      is_bookable: location?.is_bookable ?? false,
      is_active: location?.is_active ?? true,
    }),
    [location, parent],
  );
  const form = useForm<LocationValues>({
    initial,
    schema: z.object({
      code: location
        ? z.string()
        : z.string().regex(/^[A-Za-z0-9_-]{1,40}$/, t("sites.codeFormat")),
      name: z.string().trim().min(1, t("app.required")),
      type_id: z.string().min(1, t("app.required")),
    }),
    idPrefix: "loc",
  });
  // Apply type defaults for stock/bookable when the type changes on a new location.
  const onType = (id: string): void => {
    const lt = types.data?.find((x) => x.id === id);
    form.patch({
      type_id: id,
      ...(location
        ? {}
        : {
            is_stock_location: lt?.default_stock ?? false,
            is_bookable: lt?.default_bookable ?? false,
          }),
    });
  };
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const common = {
        name: v.name.trim(),
        parent_id: nullable(v.parent_id),
        capacity: numberOrNull(v.capacity),
        is_stock_location: v.is_stock_location,
        is_bookable: v.is_bookable,
        is_active: v.is_active,
      };
      const body: LocationIn | LocationPatch = location
        ? { ...common, type_id: v.type_id }
        : { ...common, site_id: site.id, type_id: v.type_id, code: v.code.trim(), attributes: {} };
      await save.mutateAsync({ id: location?.id, body });
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={location ? t("sites.editLocation") : t("sites.newLocation")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="loc-code" label={t("app.code")} error={form.errors.code}>
          <Input {...form.field("code")} disabled={!!location} />
        </Field>
        <Field id="loc-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        <Field id="loc-type_id" label={t("app.type")} error={form.errors.type_id}>
          <Select
            id="loc-type_id"
            value={form.values.type_id}
            onChange={(e) => onType(e.target.value)}
          >
            <option value="">—</option>
            {(types.data ?? []).map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="loc-parent_id" label={t("sites.parentLocation")}>
          <Select
            id="loc-parent_id"
            value={form.values.parent_id}
            onChange={(e) => form.set("parent_id", e.target.value)}
          >
            <option value="">{t("sites.topLevel")}</option>
            {locations
              .filter((l) => l.id !== location?.id)
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {"\u00a0".repeat(l.depth * 2)}
                  {l.code} · {l.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field id="loc-capacity" label={t("sites.capacity")} error={form.errors.capacity}>
          <Input type="number" min="0" {...form.field("capacity")} />
        </Field>
      </div>
      <div className="flex flex-wrap gap-4">
        <Checkbox
          id="loc-stock"
          label={t("sites.stockLocation")}
          checked={form.values.is_stock_location}
          onChange={(e) => form.set("is_stock_location", e.target.checked)}
        />
        <Checkbox
          id="loc-bookable"
          label={t("sites.bookable")}
          checked={form.values.is_bookable}
          onChange={(e) => form.set("is_bookable", e.target.checked)}
        />
        <Checkbox
          id="loc-active"
          label={t("app.active")}
          checked={form.values.is_active}
          onChange={(e) => form.set("is_active", e.target.checked)}
        />
      </div>
    </FormDialog>
  );
}

/** Location tree for one site. */
function LocationTree({ site, canManage }: { site: Site; canManage: boolean }): ReactElement {
  const { t } = useTranslation();
  const q = useLocationsOf(site.id);
  const remove = useDeleteLocation();
  const [dialog, setDialog] = useState<{ location?: Location; parent?: Location } | null>(null);
  const byParent = useMemo(() => {
    const map = new Map<string | null, Location[]>();
    for (const l of q.data ?? []) {
      const list = map.get(l.parent_id) ?? [];
      list.push(l);
      map.set(l.parent_id, list);
    }
    return map;
  }, [q.data]);

  const renderNode = (l: Location): ReactElement => (
    <li key={l.id}>
      <div
        className="flex flex-wrap items-center gap-2 py-1 [overflow-wrap:anywhere]"
        style={{ paddingInlineStart: Math.min(l.depth, 4) * 16 }}
      >
        <span className="font-mono text-xs text-muted-foreground">{l.code}</span>
        <span className={l.is_active ? "text-sm" : "text-sm line-through"}>{l.name}</span>
        <Badge tone="outline">{l.type_key}</Badge>
        {l.is_stock_location ? <Badge tone="info">{t("sites.stock")}</Badge> : null}
        {l.is_bookable ? <Badge tone="success">{t("sites.bookableShort")}</Badge> : null}
        {l.capacity !== null ? (
          <span className="text-xs text-muted-foreground">cap {l.capacity}</span>
        ) : null}
        {canManage ? (
          <span className="ms-auto flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setDialog({ parent: l })}>
              {t("app.add")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDialog({ location: l })}>
              {t("app.edit")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                if (globalThis.confirm(t("app.confirmDelete"))) {
                  remove.mutate(l.id);
                }
              }}
            >
              {t("app.delete")}
            </Button>
          </span>
        ) : null}
      </div>
      {(byParent.get(l.id) ?? []).length ? (
        <ul>{(byParent.get(l.id) ?? []).map(renderNode)}</ul>
      ) : null}
    </li>
  );

  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  return (
    <div>
      {dialog ? (
        <LocationDialog
          site={site}
          locations={q.data}
          location={dialog.location}
          parent={dialog.parent}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {canManage ? (
        <div className="mb-2 flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setDialog({})}>
            {t("sites.newLocation")}
          </Button>
        </div>
      ) : null}
      {remove.isError ? (
        <p role="alert" className="mb-2 text-sm text-destructive">
          {remove.error.message}
        </p>
      ) : null}
      {q.data.length === 0 ? (
        <EmptyState message={t("sites.noLocations")} />
      ) : (
        <ul className="rounded-md border p-2">{(byParent.get(null) ?? []).map(renderNode)}</ul>
      )}
    </div>
  );
}

/** Per-tenant location types list and creation dialog. */
function LocationTypesDialog({
  canManage,
  onClose,
}: {
  canManage: boolean;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const types = useLocationTypes();
  const create = useCreateLocationType();
  const form = useForm({
    initial: {
      key: "",
      name: "",
      name_ar: "",
      default_stock: false,
      default_bookable: false,
    },
    schema: z.object({
      key: z.string().regex(/^[a-z0-9_-]{2,40}$/, t("roles.keyFormat")),
      name: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "ltype",
  });
  const submit = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    const ok = await form.submit((v) =>
      create.mutateAsync({
        key: v.key,
        name: v.name.trim(),
        name_i18n: { en: v.name.trim(), ...(v.name_ar ? { ar: v.name_ar } : {}) },
        icon: "",
        default_stock: v.default_stock,
        default_bookable: v.default_bookable,
      }),
    );
    if (ok) {
      form.patch({ key: "", name: "", name_ar: "", default_stock: false, default_bookable: false });
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("sites.locationTypes")}
      footer={<Button onClick={onClose}>{t("app.close")}</Button>}
      wide
    >
      <div className="space-y-4">
        <ul className="max-h-60 divide-y overflow-y-auto rounded-md border p-2 text-sm">
          {(types.data ?? []).map((lt: LocationType) => (
            <li key={lt.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <div>
                <span className="font-medium">{lt.name}</span>{" "}
                <span className="font-mono text-xs text-muted-foreground">({lt.key})</span>{" "}
                {lt.is_system ? <Badge tone="outline">{t("roles.system")}</Badge> : null}
              </div>
              <div className="flex gap-1">
                {lt.default_stock ? <Badge tone="info">{t("sites.stock")}</Badge> : null}
                {lt.default_bookable ? (
                  <Badge tone="success">{t("sites.bookableShort")}</Badge>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        {canManage ? (
          <form onSubmit={(e) => void submit(e)} className="space-y-3 rounded-md border p-3">
            <h3 className="text-sm font-semibold">{t("sites.newLocationType")}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field id="ltype-key" label={t("app.key")} error={form.errors.key}>
                <Input {...form.field("key")} />
              </Field>
              <Field id="ltype-name" label={t("app.name")} error={form.errors.name}>
                <Input {...form.field("name")} />
              </Field>
              <Field id="ltype-name_ar" label={t("assetTypes.nameAr")}>
                <Input dir="rtl" {...form.field("name_ar")} />
              </Field>
            </div>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                id="ltype-default_stock"
                label={t("sites.stockLocation")}
                checked={form.values.default_stock}
                onChange={(e) => form.set("default_stock", e.target.checked)}
              />
              <Checkbox
                id="ltype-default_bookable"
                label={t("sites.bookable")}
                checked={form.values.default_bookable}
                onChange={(e) => form.set("default_bookable", e.target.checked)}
              />
            </div>
            {form.errors._ ? (
              <p role="alert" className="text-sm text-destructive">
                {form.errors._}
              </p>
            ) : null}
            <Button type="submit" size="sm" disabled={form.submitting}>
              {t("sites.newLocationType")}
            </Button>
          </form>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Sites page. */
export function SitesPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const sites = useSites();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ site?: Site } | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const manage = can("locations.site.manage");
  const selectedId = search.tab ?? sites.data?.[0]?.id;
  const selected = sites.data?.find((s) => s.id === selectedId);
  return (
    <div>
      <PageHeader
        title={t("sites.title")}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setTypesOpen(true)}>
              {t("sites.locationTypes")}
            </Button>
            {manage ? <Button onClick={() => setDialog({})}>{t("sites.newSite")}</Button> : null}
          </div>
        }
      />
      {dialog ? <SiteDialog site={dialog.site} onClose={() => setDialog(null)} /> : null}
      {typesOpen ? (
        <LocationTypesDialog canManage={manage} onClose={() => setTypesOpen(false)} />
      ) : null}
      {sites.isPending ? (
        <LoadingState />
      ) : sites.isError ? (
        <ErrorState error={sites.error} onRetry={() => void sites.refetch()} />
      ) : sites.data.length === 0 ? (
        <EmptyState message={t("sites.noSites")} />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[16rem_minmax(0,1fr)]">
          <ul className="space-y-1" aria-label={t("sites.title")}>
            {sites.data.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`w-full rounded-md border px-3 py-2 text-start text-sm hover:bg-accent ${s.id === selectedId ? "bg-accent font-medium" : ""}`}
                  aria-pressed={s.id === selectedId}
                  onClick={() =>
                    void navigate({ to: "/admin/sites", search: { ...search, tab: s.id } })
                  }
                >
                  <div className="flex items-center justify-between">
                    <span>{s.name}</span>
                    <StatusBadge value={s.kind} namespace="sites.kinds" />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {s.code} · {s.location_count ?? 0} {t("sites.locations")}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {selected ? (
            <Card>
              <CardHeader className="flex-row items-start justify-between">
                <div>
                  <CardTitle>
                    {selected.code} · {selected.name}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {t(`sites.kinds.${selected.kind}`)} · {selected.timezone || "—"} ·{" "}
                    {formatPoint(selected.geo)}
                  </p>
                </div>
                {manage ? (
                  <Button size="sm" variant="outline" onClick={() => setDialog({ site: selected })}>
                    {t("app.edit")}
                  </Button>
                ) : null}
              </CardHeader>
              <CardContent>
                <LocationTree site={selected} canManage={manage} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
